import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import type { BlockItem } from "../types";
import { haptic } from "../tg";
import AudioPlayer from "./AudioPlayer";
import VideoPlayer from "./VideoPlayer";

type ExerciseData = Extract<BlockItem, { type: "exercise" }>;

export type ExerciseCardMode = "lesson" | "homework";

// Медиа, прикреплённое к упражнению. Одна сущность из трёх — видео / аудио / фото.
// Бэк-схема пока этого не знает: пропс используется только в Test UI sandbox
// для подбора визуального варианта. Дальше — раскатаем после выбора Ильи.
// caption — общая опциональная подпись под медиа, рендерится единообразно
// через .media-caption (мелким приглушённым шрифтом, всегда снизу).
export type ExerciseMedia =
  | {
      type: "video";
      url: string;
      width?: number;
      height?: number;
      duration_seconds?: number;
      caption?: string;
    }
  | { type: "audio"; url: string; duration_seconds?: number; caption?: string }
  | { type: "image"; url: string; caption?: string };

type Props = {
  exercise: ExerciseData;
  mode: ExerciseCardMode;
  count: number;
  onComplete: () => Promise<void> | void;
  // Если задан — показывается мелким текстом над заголовком. Используется в Daily ДЗ
  // («Раздел › Урок»). В уроке не передаём — там и так понятно где мы.
  lessonLabel?: string;
  // Если задан — снятие галки «Выполнено» обнуляет счётчик на бэке.
  // Если не задан — снять галку нельзя (после клика она остаётся checked).
  // В DailyHomework пропс подключён к /homework/reset-progress; в Lesson —
  // не передаётся, там понятия «выполнено сегодня» нет.
  onResetProgress?: () => Promise<void> | void;
  // Опциональное медиа к упражнению — рендерится между title и description.
  media?: ExerciseMedia;
};

// Одна карточка упражнения с интерактивом — таймер, метроном (4 доли), счётчик,
// чекбокс «Выполнено» (выставить done=target). Чекбокс «Дневные» теперь на блоке,
// карточка его не рендерит. Используется в Lesson и DailyHomework.
export default function ExerciseCard({
  exercise,
  mode,
  count,
  onComplete,
  lessonLabel,
  onResetProgress,
  media,
}: Props) {
  const hasTimer = exercise.timer_seconds !== null && exercise.timer_seconds > 0;
  const hasMetronome =
    exercise.metronome_bpm !== null && exercise.metronome_bpm > 0;
  const hasMechanics = hasTimer || hasMetronome;

  const target = exercise.repetitions_per_day || 1;
  // В ДЗ кнопка «утвердается» когда счётчик дотянул до target.
  // В уроке счётчик безлимитный, done=false всегда.
  const done = mode === "homework" && count >= target;

  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState<number>(
    exercise.timer_seconds ?? 0,
  );
  // Текущий удар метронома 1..4 (визуальная схема такта 4/4). 0 = не запущен.
  const [beatStep, setBeatStep] = useState(0);
  const [posting, setPosting] = useState(false);

  const sounds = useSounds();
  const tickTimerRef = useRef<number | null>(null);
  const metroTimerRef = useRef<number | null>(null);

  const stopAll = () => {
    if (tickTimerRef.current !== null) {
      window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    if (metroTimerRef.current !== null) {
      window.clearInterval(metroTimerRef.current);
      metroTimerRef.current = null;
    }
    setBeatStep(0);
  };

  useEffect(() => {
    return stopAll;
  }, []);

  const reportComplete = async () => {
    if (done) return;
    setPosting(true);
    try {
      await onComplete();
      haptic("success");
    } catch (e) {
      haptic("error");
      console.warn("exercise complete failed:", (e as Error).message);
    } finally {
      setPosting(false);
    }
  };

  // Естественное окончание таймера: дотикало до 0.
  const finishTimer = () => {
    stopAll();
    setRunning(false);
    sounds.playBeep();
    if (!done) reportComplete();
  };

  const onStart = async () => {
    if (done) return;
    haptic.impact("medium");

    if (!hasMechanics) {
      await reportComplete();
      return;
    }

    setRunning(true);

    if (hasMetronome) {
      const periodMs = 60000 / (exercise.metronome_bpm as number);
      sounds.playClick();
      setBeatStep(1);
      metroTimerRef.current = window.setInterval(() => {
        sounds.playClick();
        setBeatStep((s) => (s % 4) + 1);
      }, periodMs);
    }

    if (hasTimer) {
      setRemaining(exercise.timer_seconds as number);
      tickTimerRef.current = window.setInterval(() => {
        setRemaining((r) => {
          if (r <= 1) {
            window.setTimeout(finishTimer, 0);
            return 0;
          }
          return r - 1;
        });
      }, 1000);
    }
  };

  // Стоп кнопкой: завершение «по воле» юзера.
  // - Для метронома без таймера это «успешный финал» — +1.
  // - Если есть таймер — это досрочное прерывание (Стоп ≠ дотикало), +1 НЕ начисляем.
  const onStop = () => {
    haptic.selectionChanged();
    stopAll();
    setRunning(false);
    if (hasTimer) {
      setRemaining(exercise.timer_seconds as number);
      return;
    }
    if (hasMetronome && !done) {
      reportComplete();
    }
  };

  // «Выполнено»: checked = «счётчик дотянул до target» (упражнение засчитано
  // на сегодня, без претензии на способ выполнения).
  // Постановка: вызываем reportComplete (target - count) раз — бэк делает +1 за вызов.
  // Снятие: только если родитель передал onResetProgress, иначе галка визуально возвращается.
  const allDone = count >= target;
  const selfDoneDisabled = posting || running || (allDone && !onResetProgress);

  const onSelfDoneChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    haptic.selectionChanged();
    if (checked) {
      const need = Math.max(0, target - count);
      if (need === 0) return;
      setPosting(true);
      try {
        for (let i = 0; i < need; i++) {
          await onComplete();
        }
        haptic("success");
      } catch (err) {
        haptic("error");
        console.warn("self-done complete failed:", (err as Error).message);
      } finally {
        setPosting(false);
      }
    } else {
      if (!onResetProgress) {
        // Откатываем визуально — снять без resetProgress нельзя.
        e.target.checked = true;
        return;
      }
      setPosting(true);
      try {
        await onResetProgress();
      } catch (err) {
        haptic("error");
        console.warn("self-done reset failed:", (err as Error).message);
      } finally {
        setPosting(false);
      }
    }
  };

  // Основная кнопка под таймером/метрономом — одна, на всю ширину ряда.
  //  - done    → статичная «✓ Сделано» (disabled; сброс — через чекбокс «Выполнено»).
  //  - running → «■ Стоп».
  //  - idle    → «▶ Старт» (или «✓ Готово» если механики нет — это ручное завершение).
  let btnLabel: string;
  let btnExtraClass = "";
  let btnHandler: () => void;
  let btnDisabled = false;
  if (done) {
    btnLabel = "✓ Сделано";
    btnExtraClass = "exercise-start-btn-done";
    btnHandler = () => {};
    btnDisabled = true;
  } else if (running) {
    btnLabel = "■ Стоп";
    btnExtraClass = "exercise-start-btn-stop";
    btnHandler = onStop;
  } else {
    btnLabel = hasMechanics ? "▶ Старт" : "✓ Готово";
    btnHandler = onStart;
  }

  return (
    <div className={`exercise-item${done ? " exercise-item-done" : ""}`}>
      {lessonLabel && (
        <div className="exercise-lesson-meta">{lessonLabel}</div>
      )}
      <h3 className="exercise-title">{exercise.title}</h3>
      {media && <ExerciseMediaView media={media} />}
      {exercise.description_md && (
        <div className="block-text exercise-description">
          <ReactMarkdown>{exercise.description_md}</ReactMarkdown>
        </div>
      )}

      {hasTimer && (
        <div
          className="exercise-timer-display"
          style={running ? undefined : { opacity: 0.6 }}
        >
          {formatTimer(running ? remaining : (exercise.timer_seconds as number))}
        </div>
      )}

      {hasMetronome && running && (
        <div className="exercise-metronome">
          <div className="exercise-metronome-beats">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`exercise-metronome-dot${
                  beatStep === i ? " active" : ""
                }`}
              />
            ))}
          </div>
          <div className="exercise-metronome-bpm">
            {exercise.metronome_bpm} BPM
          </div>
        </div>
      )}

      <div className="exercise-btn-row">
        <button
          type="button"
          className={`exercise-start-btn ${btnExtraClass}`}
          onClick={btnHandler}
          disabled={posting || btnDisabled}
        >
          {posting ? "…" : btnLabel}
        </button>
      </div>

      <div className="exercise-dz-progress">
        Сделано: {count} / {target}
      </div>

      <div className="exercise-dz-footer exercise-checkbox-row">
        <label className="exercise-dz-checkbox">
          <input
            type="checkbox"
            checked={allDone}
            disabled={selfDoneDisabled}
            onChange={onSelfDoneChange}
          />
          <span>Выполнено</span>
        </label>
      </div>
    </div>
  );
}

// Медиа упражнения. Видео и аудио — через готовые плееры (caption у них рендерится
// под кнопками внутри плеера). Фото — простой <figure> с border-radius и опциональной
// подписью снизу (единый стиль .media-caption).
function ExerciseMediaView({ media }: { media: ExerciseMedia }) {
  if (media.type === "video") {
    return (
      <div className="exercise-media exercise-media-video">
        <VideoPlayer
          url={media.url}
          title={null}
          width={media.width ?? null}
          height={media.height ?? null}
          caption={media.caption ?? null}
        />
      </div>
    );
  }
  if (media.type === "audio") {
    return (
      <div className="exercise-media exercise-media-audio">
        <AudioPlayer src={media.url} title={null} caption={media.caption ?? null} />
      </div>
    );
  }
  // image
  return (
    <figure className="exercise-media exercise-media-image">
      <img src={media.url} alt={media.caption ?? ""} className="exercise-image" />
      {media.caption && (
        <figcaption className="media-caption">{media.caption}</figcaption>
      )}
    </figure>
  );
}

// ----------------------------------------------------------------------------
// Звуки: HTML5 <audio> через data URI WAV.
// Web Audio API на iOS глушится при включённом silent switch — а <audio>
// element играет всегда (через media-категорию). Поэтому метронома/beep
// одинаково слышно и в обычном режиме, и при iPhone в тихом режиме.
// Один WAV-семпл генерим один раз в data URL, при тике клонируем Audio()
// и .play() — это даёт корректные накладывающиеся тики на быстрых BPM.
// ----------------------------------------------------------------------------

const SAMPLE_RATE = 44100;

function useSounds() {
  // Генерируем семплы один раз на mount.
  const data = useMemo(() => {
    const clickUrl = buildWavDataUrl(synthClick());
    const beepUrl = buildWavDataUrl(synthBeep());
    return { clickUrl, beepUrl };
  }, []);

  const playClick = () => {
    try {
      const a = new Audio(data.clickUrl);
      a.volume = 1;
      void a.play();
    } catch {
      /* noop */
    }
  };

  const playBeep = () => {
    try {
      const a = new Audio(data.beepUrl);
      a.volume = 1;
      void a.play();
    } catch {
      /* noop */
    }
  };

  return { playClick, playBeep };
}

// «Деревянный» click ~60мс: mix синусов 1500+800Hz с короткой attack/exponential decay.
function synthClick(): Float32Array {
  const durSec = 0.06;
  const n = Math.floor(durSec * SAMPLE_RATE);
  const out = new Float32Array(n);
  const peak = 0.7;
  const attackN = Math.floor(0.005 * SAMPLE_RATE);
  for (let i = 0; i < n; i++) {
    const t = i / SAMPLE_RATE;
    const hi = Math.sin(2 * Math.PI * 1500 * t) * 0.7;
    const lo = Math.sin(2 * Math.PI * 800 * t) * 0.4;
    let env: number;
    if (i < attackN) {
      env = (i / attackN) * peak;
    } else {
      // exponential decay 0.7 → 0.001 за оставшееся время
      const p = (i - attackN) / (n - attackN);
      env = peak * Math.pow(0.001 / peak, p);
    }
    out[i] = (hi + lo) * env;
  }
  return out;
}

// Финальный beep таймера: 800Hz, 250мс, треугольная огибающая.
function synthBeep(): Float32Array {
  const durSec = 0.25;
  const n = Math.floor(durSec * SAMPLE_RATE);
  const out = new Float32Array(n);
  const peak = 0.5;
  const attackN = Math.floor(0.01 * SAMPLE_RATE);
  for (let i = 0; i < n; i++) {
    const t = i / SAMPLE_RATE;
    const s = Math.sin(2 * Math.PI * 800 * t);
    let env: number;
    if (i < attackN) {
      env = (i / attackN) * peak;
    } else {
      env = peak * (1 - (i - attackN) / (n - attackN));
    }
    out[i] = s * env;
  }
  return out;
}

// Float32 (-1..1) → 16-bit PCM mono WAV → base64 data URL.
function buildWavDataUrl(samples: Float32Array): string {
  const numFrames = samples.length;
  const bytesPerSample = 2;
  const blockAlign = bytesPerSample; // mono
  const byteRate = SAMPLE_RATE * blockAlign;
  const dataSize = numFrames * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // RIFF header
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, "WAVE");
  // fmt chunk
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample
  // data chunk
  writeAscii(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let off = 44;
  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }

  // ArrayBuffer → base64. btoa требует binary string.
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(bin)}`;
}

function writeAscii(view: DataView, offset: number, s: string) {
  for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
}

function formatTimer(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
